import { model as router } from '!/router'
import { lookbehind } from '@/effector'
import { aye, F, isEmpty, T } from '@/helpers'
import { email, maxLength, password, required } from '@/validators'
import { api } from '@setplex/tria-api'
import {
  attach,
  combine,
  createEvent,
  createStore,
  sample,
  type EventCallable,
  type StoreWritable,
} from 'effector'
import { createForm } from 'effector-forms'
import { condition } from 'patronum'
import { model as session } from '~/entities/session'
import { getLandingRegionUrlFromAlias } from '~/features/landingRegion'
import { preferencesFeatureModel } from '~/features/preferences'
import { ERROR } from '~/shared/constants/errors'
import { PATH } from '~/shared/constants/path'
import * as signUpModel from '~/widgets/auth/sign-up/model'

// init page
export const init: EventCallable<{ query: URLSearchParams }> = createEvent()
export const left = createEvent()

const $qrToken = createStore('').reset(left)

sample({
  clock: init,
  filter: ({ query }) => query.has('qrAuthToken'),
  fn: ({ query }) => query.get('qrAuthToken'),
  target: $qrToken,
})

// sign in effect
export const signInFx = attach({ effect: api.user.signInFx })

export const waitFx = session.checkStatusFx

// sign in form
export const signInForm = createForm({
  fields: {
    email: {
      init: '' as string,
      rules: [required(), email()],
    },
    password: {
      init: '' as string,
      rules: [required(), password(), maxLength(64)],
    },
    rememberMe: {
      init: false as boolean,
    },
  },
  validateOn: ['submit'],
})

export const $canSubmit = combine(
  signInForm.fields.email.$value,
  signInForm.fields.password.$value,
  (email, password) => !isEmpty(email) && !isEmpty(password)
)

// send sign in request
sample({
  clock: signInForm.formValidated,
  fn: ({ email, password, rememberMe }) => ({
    email: email.trim(),
    password,
    rememberMe,
  }),
  target: signInFx,
})

export const resetErrorOnClick: EventCallable<void> = createEvent()

const resetError = lookbehind({
  source: resetErrorOnClick,
  clear: init,
  within: 1000,
})

// error to show to the user
export const $error: StoreWritable<string | null> = createStore<string | null>(
  null
)
  .on(
    signInFx.failData,
    (_, error) => error?.payload?.message || ERROR.SOMETHING_WENT_WRONG
  )
  .reset(init, resetError)

sample({
  clock: init,
  filter: ({ query }) => query.has('error'),
  fn: ({ query }) => query.get('error') ?? null,
  target: $error,
})

// after being authorized and triyng to sign in via QR code redirect to home with the same qrAuthToken
sample({
  clock: signInFx.done,
  source: $qrToken,
  filter: aye,
  fn: (qrToken: string) => `${PATH.HOME}?qrAuthToken=${qrToken}`,
  target: router.navigatePush,
})

sample({
  clock: signInFx.done,
  target: api.userSession.getInfoFx,
})

export const tryToOpenSignInPopup: EventCallable<void> = createEvent()
export const openSignInPopup: EventCallable<void> = createEvent()

export const closeSignInPopup: EventCallable<void> = createEvent()
export const checkEnableToClose: EventCallable<void> = createEvent()

// Open Sign in popup only if user not authenticated
sample({
  clock: tryToOpenSignInPopup,
  filter: session.$isNotAuthenticated,
  target: openSignInPopup,
})

condition({
  source: checkEnableToClose,
  if: $error,
  then: resetError,
  else: closeSignInPopup,
})

sample({
  clock: [init, openSignInPopup],
  target: signInForm.reset,
})

const $isSignInPage = router.$location.map(
  (location) => location?.pathname.includes(PATH.SIGN_IN) ?? false
)

sample({
  clock: [init, session.$isAuthenticated],
  source: {
    isAuthenticated: session.$isAuthenticated,
    isSignInPage: $isSignInPage,
    preference: preferencesFeatureModel.$preference,
  },
  filter: ({ isAuthenticated, isSignInPage }) =>
    Boolean(isAuthenticated && isSignInPage),
  fn: ({ preference }) => {
    const landingRegion = preference?.alias
    return `${PATH.HOME}${landingRegion ? getLandingRegionUrlFromAlias(landingRegion) : ''}`
  },
  target: router.navigatePush,
})

export const $isOpenedSignInPopup: StoreWritable<boolean> =
  createStore<boolean>(false)
    .on(openSignInPopup, T)
    .on([closeSignInPopup, signInFx.done], F)
    // close signIn popup if signUp popup opened
    .on(signUpModel.$isOpenedSignUpPopup, (state, value) =>
      value ? false : state
    )

// close signUp popup if signIn popup opened
sample({
  clock: $isOpenedSignInPopup,
  filter: $isOpenedSignInPopup,
  fn: F,
  target: signUpModel.$isOpenedSignUpPopup,
})
